import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown } from "lucide-react";
import { MaskedLine } from "./Motion";

const HERO_IMG =
  "https://images.unsplash.com/photo-1562519990-50eb51e282b2?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzd8MHwxfHNlYXJjaHwxfHx3aW5kJTIwdHVyYmluZXMlMjBsYW5kc2NhcGV8ZW58MHx8fHwxNzg0MzUxMTIxfDA&ixlib=rb-4.1.0&q=85";

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
  const textY = useTransform(scrollYProgress, [0, 1], ["0%", "-30%"]);
  const overlayOpacity = useTransform(scrollYProgress, [0, 1], [0.55, 0.85]);

  const scrollNext = () =>
    document.getElementById("about")?.scrollIntoView({ behavior: "smooth" });

  return (
    <section
      id="hero"
      ref={ref}
      className="relative flex min-h-screen items-end overflow-hidden bg-vg-primary"
      data-testid="hero-section"
    >
      {/* Parallax background image */}
      <motion.div
        style={{ y: imgY, scale: imgScale }}
        className="absolute inset-0 -z-0"
      >
        <img
          src={HERO_IMG}
          alt="Wind turbines at dusk"
          className="h-[115%] w-full object-cover"
        />
      </motion.div>
      <motion.div
        style={{ opacity: overlayOpacity }}
        className="absolute inset-0 bg-gradient-to-t from-vg-primary via-vg-primary/70 to-vg-primary/20"
      />
      <div className="grain absolute inset-0" />

      {/* Content */}
      <motion.div
        style={{ y: textY }}
        className="relative z-10 mx-auto w-full max-w-[1400px] px-6 pb-16 pt-28 lg:px-10 lg:pb-24"
      >
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6 }}
          className="mb-8 flex items-center gap-3 text-xs font-medium uppercase tracking-[0.28em] text-vg-accent"
        >
          <span className="h-px w-10 bg-vg-accent" />
          EPC · Power Transmission · Renewables · Infrastructure
        </motion.div>

        <h1 className="font-display text-[12vw] font-semibold leading-[0.94] tracking-tight text-white sm:text-[9vw] lg:text-[6.8vw]">
          <MaskedLine delay={0.15}>Building the</MaskedLine>
          <MaskedLine delay={0.3}>
            <span className="text-vg-accent">grid</span> that powers
          </MaskedLine>
          <MaskedLine delay={0.45}>a greener India.</MaskedLine>
        </h1>

        <div className="mt-10 flex flex-col items-start justify-between gap-8 lg:flex-row lg:items-end">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.75, ease: [0.16, 1, 0.3, 1] }}
            className="max-w-xl text-base leading-relaxed text-white/75 lg:text-lg"
          >
            Venus Green Renewables &amp; Energy delivers full turnkey EPC
            solutions across power transmission, renewable energy and
            large-scale infrastructure — backed by promoters with 25 years of
            proven execution.
          </motion.p>

          <motion.button
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.9, ease: [0.16, 1, 0.3, 1] }}
            onClick={scrollNext}
            data-testid="hero-scroll-btn"
            className="group flex items-center gap-4 text-sm font-medium uppercase tracking-[0.2em] text-white"
          >
            <span className="flex h-14 w-14 items-center justify-center rounded-full border border-white/30 transition-colors duration-300 group-hover:bg-white group-hover:text-vg-primary">
              <ArrowDown size={20} className="animate-bounce" />
            </span>
            Explore
          </motion.button>
        </div>
      </motion.div>
    </section>
  );
}
